import React, { useState, useEffect } from 'react';

const ContactModal = ({ isOpen, onClose, initialData }) => {
  const [contactData, setContactData] = useState({
    hero: {
      title: "",
      subtitle: "",
      background: ""
    },
    contactInfo: {
      phones: ["", ""],
      emails: ["", ""],
      address: "", // Address is a string
      openingHours: [
        { days: "", hours: "" },
        { days: "", hours: "" },
        { days: "", hours: "" }
      ]
    },
    mapEmbedUrl: "",
    cta: {
      title: "",
      buttons: [
        { text: "", link: "", style: "primary" },
        { text: "", link: "", style: "secondary" }
      ]
    }
  });

  const [activeTab, setActiveTab] = useState('hero');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchCurrentData = async () => {
      if (isOpen && !initialData) {
        try {
          setIsLoading(true);
          const response = await fetch(`${import.meta.env.VITE_API_URL}/contact/`);
          if (response.ok) {
            const data = await response.json();
            setContactData(data);
          }
        } catch (error) {
          console.error('Error fetching contact data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCurrentData();
  }, [isOpen, initialData]);

  useEffect(() => {
    if (initialData) {
      setContactData(initialData);
    }
  }, [initialData]);

  // Handle simple field changes
  const handleChange = (e, section, field) => {
    const { value } = e.target;
    setContactData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Handle direct field changes (like address)
  const handleDirectChange = (section, field, value) => {
    setContactData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Handle array changes (phones, emails)
  const handleArrayChange = (section, arrayName, index, value) => {
    setContactData(prev => {
      const newArray = [...prev[section][arrayName]];
      newArray[index] = value;
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [arrayName]: newArray
        }
      };
    });
  };

  // Handle opening hours changes
  const handleOpeningHoursChange = (index, field, value) => {
    setContactData(prev => {
      const newHours = [...prev.contactInfo.openingHours];
      newHours[index] = {
        ...newHours[index],
        [field]: value
      };
      return {
        ...prev,
        contactInfo: {
          ...prev.contactInfo,
          openingHours: newHours
        }
      };
    });
  };

  // Handle button changes
  const handleButtonChange = (index, field, value) => {
    setContactData(prev => {
      const newButtons = [...prev.cta.buttons];
      newButtons[index] = {
        ...newButtons[index],
        [field]: value
      };
      return {
        ...prev,
        cta: {
          ...prev.cta,
          buttons: newButtons
        }
      };
    });
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/contact/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactData)
      });

      if (response.ok) {
        onClose();
        window.location.reload();
      } else {
        console.error('Failed to update contact data');
        alert('Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error updating contact data:', error);
      alert('An error occurred while saving. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Loading contact data...</p>
        </div>
      </dialog>
    );
  }

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className="modal-box w-11/12 max-w-5xl max-h-[90vh] relative">
        <button onClick={onClose} className="btn btn-sm btn-circle absolute right-2 top-2">✕</button>
        <h3 className="font-bold text-lg mb-6">Edit Contact Section</h3>
        
        {/* Tab Navigation */}
        <div className="tabs tabs-boxed mb-6">
          <button 
            className={`tab ${activeTab === 'hero' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('hero')}
          >
            Hero Section
          </button>
          <button 
            className={`tab ${activeTab === 'contactInfo' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('contactInfo')}
          >
            Contact Info
          </button>
          <button 
            className={`tab ${activeTab === 'map' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('map')}
          >
            Map
          </button>
          <button 
            className={`tab ${activeTab === 'cta' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('cta')}
          >
            CTA
          </button>
        </div>
        
        <div className="overflow-y-auto max-h-[60vh] pr-2">
          {/* Hero Tab */}
          {activeTab === 'hero' && (
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Hero Title</span>
                </label>
                <input 
                  type="text" 
                  value={contactData.hero?.title || ""} 
                  onChange={(e) => handleChange(e, 'hero', 'title')} 
                  className="input input-bordered w-full" 
                />
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Hero Subtitle</span>
                </label>
                <input 
                  type="text" 
                  value={contactData.hero?.subtitle || ""} 
                  onChange={(e) => handleChange(e, 'hero', 'subtitle')} 
                  className="input input-bordered w-full" 
                />
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Background Gradient</span>
                </label>
                <input 
                  type="text" 
                  value={contactData.hero?.background || ""} 
                  onChange={(e) => handleChange(e, 'hero', 'background')} 
                  className="input input-bordered w-full" 
                  placeholder="linear-gradient(to right, #0760ad, #098a48)"
                />
              </div>
            </div>
          )}
          
          {/* Contact Info Tab */}
          {activeTab === 'contactInfo' && (
            <div className="space-y-6">
              <div className="card bg-base-100 shadow-sm p-4">
                <h4 className="font-bold mb-4">Phone Numbers</h4>
                {contactData.contactInfo?.phones?.map((phone, index) => (
                  <div key={index} className="form-control mb-3">
                    <label className="label">
                      <span className="label-text">Phone {index + 1}</span>
                    </label>
                    <input 
                      type="text" 
                      value={phone} 
                      onChange={(e) => handleArrayChange('contactInfo', 'phones', index, e.target.value)} 
                      className="input input-bordered w-full" 
                    />
                  </div>
                ))}
              </div>
              
              <div className="card bg-base-100 shadow-sm p-4">
                <h4 className="font-bold mb-4">Email Addresses</h4>
                {contactData.contactInfo?.emails?.map((email, index) => (
                  <div key={index} className="form-control mb-3">
                    <label className="label">
                      <span className="label-text">Email {index + 1}</span>
                    </label>
                    <input 
                      type="email" 
                      value={email} 
                      onChange={(e) => handleArrayChange('contactInfo', 'emails', index, e.target.value)} 
                      className="input input-bordered w-full" 
                    />
                  </div>
                ))}
              </div>
              
              <div className="card bg-base-100 shadow-sm p-4">
                <h4 className="font-bold mb-4">Address</h4>
                <div className="form-control">
                  <textarea 
                    value={contactData.contactInfo?.address || ""} 
                    onChange={(e) => handleDirectChange('contactInfo', 'address', e.target.value)} 
                    className="textarea textarea-bordered w-full" 
                    rows="3"
                  />
                </div>
              </div>
              
              <div className="card bg-base-100 shadow-sm p-4">
                <h4 className="font-bold mb-4">Opening Hours</h4>
                {contactData.contactInfo?.openingHours?.map((hours, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Days</span>
                      </label>
                      <input 
                        type="text" 
                        value={hours.days} 
                        onChange={(e) => handleOpeningHoursChange(index, 'days', e.target.value)} 
                        className="input input-bordered w-full" 
                      />
                    </div>
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Hours</span>
                      </label>
                      <input 
                        type="text" 
                        value={hours.hours} 
                        onChange={(e) => handleOpeningHoursChange(index, 'hours', e.target.value)} 
                        className="input input-bordered w-full" 
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Map Tab */}
          {activeTab === 'map' && (
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Map Embed URL</span>
                </label>
                <textarea 
                  value={contactData.mapEmbedUrl || ""} 
                  onChange={(e) => handleDirectChange('mapEmbedUrl', 'mapEmbedUrl', e.target.value)} 
                  className="textarea textarea-bordered w-full" 
                  rows="5"
                />
              </div>
            </div>
          )}
          
          {/* CTA Tab */}
          {activeTab === 'cta' && (
            <div className="space-y-6">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">CTA Title</span>
                </label>
                <input 
                  type="text" 
                  value={contactData.cta?.title || ""} 
                  onChange={(e) => handleChange(e, 'cta', 'title')} 
                  className="input input-bordered w-full" 
                />
              </div>
              
              <div className="card bg-base-100 shadow-sm p-4">
                <h4 className="font-bold mb-4">Buttons</h4>
                {contactData.cta?.buttons?.map((button, index) => (
                  <div key={index} className="card bg-base-200 p-4 mb-4">
                    <h5 className="font-medium mb-3">Button {index + 1}</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Button Text</span>
                        </label>
                        <input 
                          type="text" 
                          value={button.text} 
                          onChange={(e) => handleButtonChange(index, 'text', e.target.value)} 
                          className="input input-bordered w-full" 
                        />
                      </div>
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Button Link</span>
                        </label>
                        <input 
                          type="text" 
                          value={button.link} 
                          onChange={(e) => handleButtonChange(index, 'link', e.target.value)} 
                          className="input input-bordered w-full" 
                        />
                      </div>
                    </div>
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Button Style</span>
                      </label>
                      <select
                        value={button.style} 
                        onChange={(e) => handleButtonChange(index, 'style', e.target.value)} 
                        className="select select-bordered w-full"
                      >
                        <option value="primary">Primary</option>
                        <option value="secondary">Secondary</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="modal-action mt-6">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            Save Changes
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default ContactModal;